import { useQueryClient } from "@tanstack/react-query";
import { useUpdateSeminarSessionMutation } from "api/mutations/forumSeminarSessions";
import { useSeminarsSessionsQuery } from "api/queries/forumSeminarSessions";
import KEYS from "api/queries/keys";
import DateRenderer from "components/DateRenderer";
import { Switch, Tag } from "components/styleguide";
import Table from "components/styleguide/components/Table";
import { ForumSeminarSessionWithTimeslotSession } from "generated/api";
import { toNumber } from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { buildColumn } from "utils/columnUtils";

interface SeminarDetailsTabProps {
  seminar: ForumSeminarSessionWithTimeslotSession;
}

const SeminarDetailsTab = ({ seminar }: SeminarDetailsTabProps) => {
  const { t } = useTranslation();
  const forumId = toNumber(useParams().id);
  const queryClient = useQueryClient();

  const updateMutation = useUpdateSeminarSessionMutation();

  const { data: { data: sessions = [] } = {}, isLoading } = useSeminarsSessionsQuery(
    {
      forumId,
      forumSeminarId: seminar?.id,
    },
    { enabled: !!seminar?.id },
  );

  const updateSeminarSession = (session: ForumSeminarSessionWithTimeslotSession) => {
    if (!updateMutation.isLoading) {
      updateMutation.mutateAsync(
        {
          forumId,
          forumSeminarId: seminar?.id,
          forumSeminarSessionId: session.id,
          forumSeminarSessionRequestModel: {
            forumMeetingFacilityId: session.forumMeetingFacilityId,
            timeSlotSessionId: session.timeSlotSessionId,
            places: session.places,
            includeMeal: session.includeMeal,
            speakerIds:
              session.speakers?.map((s) => s.speakerId).filter((id): id is number => typeof id === "number") ?? null,
            isDisplayedOnCustomerPortal: session.isDisplayedOnCustomerPortal,
          },
        },
        {
          onError: () => {
            toast.error(t("Something went wrong"));
          },
          onSettled: () => {
            queryClient.invalidateQueries([KEYS.GET_SEMINAR_SESSIONS]);
          },
        },
      );
    }
  };

  const columns = [
    buildColumn(t("Session Start and End Time"), "startTime", {
      render: (_: string, { startTime, endTime }: ForumSeminarSessionWithTimeslotSession) => (
        <>
          <DateRenderer date={startTime} showTime wallClock /> - <DateRenderer date={endTime} showTime wallClock />
        </>
      ),
    }),
    buildColumn(t("Speakers"), "speakers", {
      render: (_: unknown, record: ForumSeminarSessionWithTimeslotSession) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
          {record.speakers?.map((speaker) => (
            <Tag key={speaker.speakerId} style={{ borderRadius: "20px", marginTop: "5px" }} color={"default"}>
              {speaker.speakerName}
            </Tag>
          ))}
        </div>
      ),
    }),
    buildColumn(t("Display on Customer Portal"), "isDisplayedOnCustomerPortal", {
      ellipsis: true,
      render: (value: boolean, record: ForumSeminarSessionWithTimeslotSession) => (
        <Switch
          checked={value}
          onChange={() => updateSeminarSession({ ...record, isDisplayedOnCustomerPortal: !value })}
        />
      ),
    }),
  ];

  return (
    <Table
      id="seminarSessionsTable"
      className="seminarSessionsTable"
      dataSource={sessions}
      columns={columns}
      bordered
      loading={isLoading || updateMutation.isLoading}
      rowKey="id"
    />
  );
};

export default SeminarDetailsTab;
